<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('studentSemesterExamList.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('toHomePage'), href: '/' },
        { text: $t('studentSemesterExamList.pageTitle'), active: true }
      ]" />
    </div>
    <div class="row">
      <div class="col-md-3">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loading">
              <form>
                <fieldset>
                  <legend>{{ $t('studentSemesterExamList.criteria') }}</legend>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('cashCollectionByCashier.branchName')
                      }}</label>
                      <a-select style="width: 100%" v-model.number="model.branch_id" :options="branches"></a-select>
                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('studentSemesterExamList.acYear')
                      }}</label>
                      <a-select v-model.number="model.ac_year_id" :options="ac_year_list" @change="acYearChange" />

                      <div class="validate" v-if="errors.has('ac_year_id')">
                        {{ errors.first('ac_year_id') }}
                      </div>
                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('studentSemesterExamList.subProgram')
                      }}</label>
                      <a-select style="width: 100%" v-model="model.acps_id" mode="multiple" :options="sub_programs"
                        :show-search="true" :filter-option="filterOption" @change="onSubChange"
                        :disabled="!model.ac_year_id" />
                      <div class="validate" v-if="errors.has('acps_id')">
                        {{ errors.first('acps_id') }}
                      </div>
                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('finalResult.Degree') }}</label>
                      <a-select style="width: 100%" v-model="model.degree_id" mode="multiple" :options="levels"
                        :filter-option="filterOption"></a-select>
                      <div class="validate" v-if="errors.has('level_id')">
                        {{ errors.first('level_id') }}
                      </div>
                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('studentSemesterExamList.acTerm')
                      }}</label>
                      <a-select style="width: 100%" v-model="model.ac_term_id" :options="ac_terms" mode="multiple"
                        :show-search="true" :filter-option="filterOption" @change="acTermChange" />
                      <div class="validate" v-if="errors.has('ac_term_id')">
                        {{ errors.first('ac_term_id') }}
                      </div>
                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('studentSemesterExamList.classOperation') }}</label>
                      <a-select v-model="model.class_id" mode="multiple" :options="class_options" :show-search="true"
                        :filter-option="filterOption" @change="onClassChange" />

                      <div class="validate" v-if="errors.has('class_id')">
                        {{ errors.first('class_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <div v-for="(item, index) in model.selected_classes_time" :key="item.class_id"
                        class="class-time-wrapper"
                        style="border: 1px solid #eee; padding: 10px; margin-bottom: 10px; border-radius: 5px;">
                        <div class="row">
                          <div class="col-md-12">
                            <strong style="color: #1890ff;">{{ item.class_name }}</strong>
                          </div>
                        </div>

                        <div class="row form-group">
                          <div class="col-md-6">
                            <label>{{ $t('studentSemesterExamList.examTime') }} (From)</label>
                            <ts-time-picker-input v-model="item.study_time_from"></ts-time-picker-input>
                          </div>
                          <div class="col-md-6">
                            <label>{{ $t('studentSemesterExamList.examTimeTo') }} (To)</label>
                            <ts-time-picker-input v-model="item.study_time_to"></ts-time-picker-input>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Room -->
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('studentSemesterExamList.room') }}</label>

                      <a-select style="width: 100%" v-model="model.room_ids" mode="multiple" :show-search="true"
                        :placeholder="$t('all')" :disabled="rooms.length === 0" @change="onRoomChange">
                        <a-select-option v-for="room in rooms" :key="room.room_id" :value="room.room_id">
                          {{ room.room_name }}
                        </a-select-option>
                      </a-select>

                      <!-- Dynamic Note per Room -->
                      <div v-for="roomId in model.room_ids" :key="roomId"
                        style="display: flex; align-items: center; gap: 8px; margin-top: 6px;">
                        <span style="flex: 1 1 55%; font-size: 13px; color: #555;">
                          {{ getRoomName(roomId) }}
                        </span>
                        <a-input style="flex: 1 1 45%" v-model="model.room_notes[roomId]"
                          :placeholder="$t('studentSemesterExamList.roomNote')" />
                      </div>

                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('studentSemesterExamList.batch') }}</label>
                      <input type="text" class="form-control" v-model="model.promotion" />
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <ts-checkbox v-model="model.attendanc_issue">{{
                        $t('studentSemesterExamList.attendanceProblem')
                      }}</ts-checkbox>
                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-6 text-right">
                      <ts-button class="btn-block" outline color="success" @click.prevent="exportExcel"
                        :waiting="exporting">
                        {{ $t('excel') }}
                      </ts-button>
                    </div>
                    <div class="col-md-6 text-right">
                      <ts-button class="btn-block" color="primary" @click.prevent="preview" :waiting="waiting">
                        {{ $t('preview') }}
                      </ts-button>
                    </div>
                  </div>

                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>

      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('studentList.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import * as FileDownload from 'downloadjs'
import moment from 'moment'

export default {
  name: 'studentSemesterExamList',

  data() {
    return {
      loading: false,
      errors: new Errors(),
      waiting: false,
      exporting: false,
      src: '',
      sub_programs: [],
      ac_terms: [],
      ac_year_list: [],
      classList: [],
      class_options: [],
      branches: [],
      levels: [],
      rooms: [],
      model: {
        branch_id: null,
        ac_year_id: null,
        ac_term_id: [],
        acps_id: [],
        class_id: [],
        selected_classes_time: [],
        promotion: null,
        degree_id: [],
        attendanc_issue: false,
        room_ids: [],
        room_notes: {},
        study_time_from: moment().format('hh:mm A'),
        study_time_to: moment().format('hh:mm A')
      }
    }
  },

  computed: {
    ...mapState('reportAcademic', ['studentStatus'])
  },

  created() {
    this.fetchData()
  },

  methods: {
    ...mapActions('reportAcademic', [
      'getBranch',
      'getYear',
      'getSubProgram',
      'getTerm',
      'getClass',
      'studentSemesterExamList',
      'getLevel'
    ]),

    // shared filter-option function
    filterOption(input, option) {
      return option.componentOptions.children[0].text
        .toLowerCase()
        .indexOf(input.toLowerCase()) >= 0
    },

    async fetchData() {
      await this.getBranch()
        .then(response => {
          this.branches = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
          this.model.branch_id = this.$store.state.authUser.default_branch_id
        })

      await this.getSubProgram()
        .then(response => {
          this.sub_programs = response.data.map(el => ({
            value: el.acps_id,
            label: el.acps_name_foreign
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })

      await this.getYear({
        filter: {
          ac_status_id: '1,2,3',
          branch_id: this.model.branch_id
        },
        branch_id: [this.model.branch_id]
      })
        .then(response => {
          this.ac_year_list = _.sortBy(response.data, ['ac_status_id']).map(el => ({
            value: el.ac_year_id,
            label:
              el.ac_year +
              (el.ac_status_id == 1
                ? ' ( Current ) '
                : el.ac_status_id == 2
                  ? ' ( History ) '
                  : ' ( Next ) ')
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },

    acYearChange(ac_year_id) {
      this.model.class_id = []
      this.classList = []
      this.rooms = []
      this.model.room_ids = []
      this.model.room_notes = {}
      this.loading = true

      this.getClass({
        filter: {
          ac_year_id: ac_year_id,   
          branch_id: this.model.branch_id
        },
        fields: {
          academic_classes: 'class_id,class_name,teacher_id,room_id',
          room: 'room_name'
        },
        include: 'room,teacher'
      })
        .then(response => {
          this.classList = response.data
          this.class_options = response.data.map(el => ({
            value: el.class_id,
            label: el.class_name + ' | ' + el.teacher.full_name_en
          }))
        })
        .finally(() => { this.loading = false })
    },
    async onSubChange(acps_ids) {
      this.model.ac_term_id = []
      this.model.degree_id = []
      this.model.class_id = []
      this.classList = []
      this.rooms = []
      this.model.room_ids = []
      this.model.room_notes = {}
      this.levels = []
      this.ac_terms = []
      this.class_options = []

      if (!acps_ids || acps_ids.length === 0) return

      this.loading = true

      await Promise.all([

        this.getTerm({
          filter: {
            acps_id: acps_ids.join(','),
            ac_status_id: '1,2',
            ac_year_id: this.model.ac_year_id
          }
        }).then(response => {
          this.ac_terms = response.data.map(el => ({
            value: el.ac_term_id,
            label: el.term_name + (el.ac_status_id == 1 ? ' ( Current ) ' : ' ( History ) ')
          }))
        }),

        // 2. Load Levels
        this.getLevel({
          filter: { acps_id: acps_ids.join(',') }
        }).then(response => {
          this.levels = response.data.map(el => ({
            value: el.level_id,
            label: el.level_name_foreign
          }))
        }),

        this.getClass({
          filter: {
            ac_year_id: this.model.ac_year_id,
            acps_id: acps_ids.join(','),
            branch_id: this.model.branch_id
          },
          fields: {
            academic_classes: 'class_id,class_name,teacher_id,room_id',
            room: 'room_name'
          },
          include: 'room,teacher'
        }).then(response => {
          this.classList = response.data
          this.class_options = response.data.map(el => ({
            value: el.class_id,
            label: el.class_name + ' | ' + el.teacher.full_name_en
          }))
        })

      ])
        .catch(error => { this.$toasted.error(error.message) })
        .finally(() => { this.loading = false })
    },

    acTermChange(ac_term_ids) {
      this.model.class_id = []
      this.classList = []
      this.rooms = []
      this.model.room_ids = []
      this.model.room_notes = {}

      if (!ac_term_ids || ac_term_ids.length === 0) {
        this.class_options = []
        return
      }

      this.loading = true

      this.getClass({
        filter: {
          ac_year_id: this.model.ac_year_id,
          ac_term_id: ac_term_ids.join(','),
          acps_id: this.model.acps_id.join(','),
          branch_id: this.model.branch_id
        },
        fields: {
          academic_classes: 'class_id,class_name,teacher_id,weekday_type_id,ac_st_id',
          academic_study_times: 'ac_st_id,study_time',
          teacher: 'teacher_id,fname_foreign,lname_foreign',
          weekday_type: 'id,weekday_type'
        },
        include: 'teacher,weekday_type,academic_study_times',
        page: -1
      })
        .then(response => {
          this.classList = response.data
          this.class_options = response.data.map(el => ({
            value: el.class_id,
            label:
              el.class_name +
              ' | ' +
              (el.teacher
                ? el.teacher.fname_foreign + ' ' + el.teacher.lname_foreign
                : '')
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onClassChange(classIds) {
      this.rooms = []
      this.model.room_ids = []
      this.model.room_notes = {}

      const newClassTimeList = []

      classIds.forEach(id => {
        const foundClass = this.classList.find(c => c.class_id === id)
        if (foundClass) {
          let from = moment().format('hh:mm A')
          let to = moment().format('hh:mm A')

          let studyTimeStr = ''
          if (Array.isArray(foundClass.academic_study_times) && foundClass.academic_study_times.length > 0) {
            studyTimeStr = foundClass.academic_study_times[0].study_time
          } else if (foundClass.academic_study_times) {
            studyTimeStr = foundClass.academic_study_times.study_time
          }

          if (studyTimeStr && studyTimeStr.includes('-')) {
            const parts = studyTimeStr.split('-')
            from = parts[0].trim()
            to = parts[1].trim()
          }

          const oldData = this.model.selected_classes_time.find(t => t.class_id === id)

          newClassTimeList.push({
            class_id: id,
            class_name: foundClass.class_name,
            study_time_from: oldData ? oldData.study_time_from : from,
            study_time_to: oldData ? oldData.study_time_to : to
          })
        }
      })

      this.model.selected_classes_time = newClassTimeList

      if (classIds.length > 0) {
        this.loading = true
        this.$store.dispatch('reportAcademic/getRoom', { filter: { class_id: classIds.join(',') } })
          .then(response => { this.$set(this, 'rooms', response.data) })
          .finally(() => { this.loading = false })
      }
    },
    onRoomChange(selectedIds) {
      const updatedNotes = {}
      selectedIds.forEach(id => {
        updatedNotes[id] = this.model.room_notes[id] || null
      })
      this.model.room_notes = updatedNotes
    },

    getRoomName(roomId) {
      const room = this.rooms.find(r => r.room_id === roomId)
      return room ? room.room_name : ''
    },

    preview() {
      this.waiting = true
      this.src = ''
      this.errors = new Errors()

      const roomNames = {}
      this.model.room_ids.forEach(roomId => {
        const room = this.rooms.find(r => r.room_id === roomId)
        if (room) {
          roomNames[roomId] = room.room_name
        }
      })
      const classTimes = {}
      if (this.model.selected_classes_time && this.model.selected_classes_time.length > 0) {
        this.model.selected_classes_time.forEach(item => {
          classTimes[item.class_id] = {
            from: item.study_time_from,
            to: item.study_time_to
          }
        })
      }
      const payload = {
        ...this.model,
        ac_term_id: Array.isArray(this.model.ac_term_id)
          ? this.model.ac_term_id  
          : [this.model.ac_term_id],
        room_names: roomNames,
        class_times: classTimes
      }
      this.studentSemesterExamList(payload)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()

      this.$store
        .dispatch('reportAcademic/studentSemesterExamListExcel', this.model)
        .then(response => {
          const disposition = response.headers['content-disposition']
          let filename = 'export.xlsx'
          if (disposition && disposition.indexOf('filename=') !== -1) {
            filename = disposition.split('filename=')[1].replace(/"/g, '')
          }
          FileDownload(response.data, filename)
        })
        .catch(error => {
          if (error.response && error.response.data) {
            const reader = new FileReader()
            reader.onload = () => {
              try {
                const err = JSON.parse(reader.result)
                this.$toasted.error(err.message || 'Export failed')
                this.errors = new Errors(err.errors || {})
              } catch (e) {
                this.$toasted.error('Export failed.')
              }
            }
            reader.readAsText(error.response.data)
          } else {
            this.$toasted.error('Network error.')
          }
        })
        .finally(() => {
          this.exporting = false
        })
    }
  }
}
</script>